import {
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuOption,
  PlusIcon,
  Text,
  TextField,
  TrashIcon,
  toaster,
} from 'components'
import { useAlert, useCurrentUser, useWorkspace } from 'components/providers'
import isEqual from 'lodash-es/isEqual'
import { useEffect, useState } from 'react'
import { WorkspaceProperty, WorkspacePropertyType } from 'types'
import { randomString, useBreakpoint } from 'utils'
import { updateWorkspaceProperties } from 'utils/callables'
import { cleanDcKey } from '../utils'

export default function WorkspacePropertiesPage() {
  const breakpoint = useBreakpoint()
  const { workspace } = useWorkspace()
  const user = useCurrentUser()
  const { confirm } = useAlert()
  const [isLoading, setIsLoading] = useState(false)

  const properties = workspace?.properties || []
  const [draft, setDraft] = useState<WorkspaceProperty[]>(properties)

  useEffect(() => {
    if (!workspace) return

    setDraft(workspace.properties ?? [])
  }, [workspace])

  function addProperty() {
    const newProperty: WorkspaceProperty = { title: '', type: 'text', id: randomString() }
    setDraft([...draft, newProperty])
  }

  function removeProperty(index: number) {
    setDraft(draft.filter((_, i) => i !== index))
  }

  function updateProperty(index: number, body: Partial<WorkspaceProperty>) {
    const updatedProperties = [...draft]
    updatedProperties[index] = { ...updatedProperties[index], ...body }
    setDraft(updatedProperties)
  }

  async function saveProperties() {
    if (!workspace || !user) return

    const cleaned = draft.filter(prop => prop.title.trim() !== '')
    const currentProperties = workspace.properties || []

    const removedProperties = currentProperties.filter(
      current => !cleaned.some(prop => prop.id === current.id)
    )
    if (removedProperties.length > 0) {
      const confirmed = await confirm(
        `Remove "${removedProperties.map(p => p.title).join('", "')}". This will permanently delete all associated data from existing leads. This action cannot be undone.`,
        {
          okText: 'Remove Properties',
          cancelText: 'Cancel',
          okButtonProps: { colorScheme: 'red' },
        }
      )

      if (!confirmed) return
    }

    setIsLoading(true)
    try {
      await updateWorkspaceProperties({ workspaceId: workspace.id, properties: cleaned })
      toaster.create({ title: 'Properties updated', type: 'success' })
    } catch (error) {
      console.error('Failed to update properties:', error)
      toaster.create({ title: 'Failed to update all properties', type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  function resetProperties() {
    setDraft(properties)
  }

  const isDirty = !isEqual(draft, workspace?.properties ?? [])
  const hasInvalidProperties = draft.some(prop => !isValidJSONKey(prop.title))
  const duplicateTitles = draft.filter(prop =>
    draft.some(p => p.title === prop.title && p.id !== prop.id)
  )

  return (
    <Flex overflow="auto">
      <Flex
        direction="column"
        gap={4}
        flex="auto"
        p={breakpoint < 2 ? 4 : 0}
        pr={5}
        mb={3}
        maxW="800px"
      >
        <Flex direction="column">
          <Text textStyle="h3" pt={2}>
            Properties
          </Text>
          <Text textStyle="h6">
            Define custom properties for your workspace that can be used across different features.
          </Text>
        </Flex>

        <Box>
          <Flex direction="column" gap={4}>
            {draft.map((property, index) => {
              const isDuplicate = duplicateTitles.some(p => p.title === property.title)
              return (
                <Property
                  key={index}
                  property={property}
                  isDuplicate={isDuplicate}
                  onRemove={() => removeProperty(index)}
                  onChange={p => updateProperty(index, p)}
                />
              )
            })}

            {draft.length === 0 && (
              <Flex
                direction="column"
                align="center"
                justify="center"
                py={8}
                border="2px dashed"
                borderColor="gray.300"
                borderRadius="md"
              >
                <Text color="gray.500" mb={2}>
                  No properties defined yet
                </Text>
                <Text color="gray.400" fontSize="sm">
                  Click "Add Property" to create your first property
                </Text>
              </Flex>
            )}
          </Flex>
        </Box>

        <Flex gap={3} align="center">
          <Button leftIcon={<PlusIcon />} onClick={addProperty} variant="outline">
            Add Property
          </Button>

          {isDirty && (
            <>
              <Button
                onClick={saveProperties}
                palette="primary"
                loading={isLoading}
                disabled={hasInvalidProperties || duplicateTitles.length > 0}
              >
                Save Changes
              </Button>
              <Button onClick={resetProperties} variant="ghost" disabled={isLoading}>
                Reset
              </Button>
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

interface PropertyProps {
  property: WorkspaceProperty
  isDuplicate: boolean
  onChange: (property: WorkspaceProperty) => void
  onRemove: () => void
}

function Property({ property, isDuplicate, onChange, onRemove }: PropertyProps) {
  return (
    <Flex gap={3} align="end">
      <TextField
        label="Property Name"
        placeholder="Enter property name"
        value={property.title}
        onChange={e => onChange({ ...property, title: cleanDcKey(e.target.value) })}
        flex="1"
        isError={isDuplicate}
        helperText={isDuplicate ? 'Property name must be unique' : undefined}
      />

      <Box width="150px">
        <Text fontSize="sm" fontWeight="medium" mb={2}>
          Type
        </Text>
        <Menu
          buttonLabel={property.type.charAt(0).toUpperCase() + property.type.slice(1)}
          options={propertyOptions}
          onChange={type => onChange({ ...property, type })}
          value={property.type}
        />
      </Box>

      <IconButton
        aria-label="Remove property"
        icon={<TrashIcon />}
        colorScheme="red"
        variant="ghost"
        onClick={onRemove}
      />
    </Flex>
  )
}

const propertyOptions: MenuOption<WorkspacePropertyType>[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
]

function isValidJSONKey(key: string): boolean {
  if (!key.trim()) return true

  const invalidChars = /(?:^\d+|[\x00-\x1F"'\\])/
  return !invalidChars.test(key)
}
